import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { REPLENISHMENT_POLICIES } from "../../../api/manufacturing";
import {
  REPLENISHMENT_ZONES,
  ReplenishmentClient,
  replenishmentKeys,
  type ReplenishmentLineDTO,
} from "../../../api/replenishment";
import { SupplierLink } from "../../../components/SupplierLink";
import { policyLabel } from "./MaterialStory";
import { formatCad, formatCover, formatQuantity, ZoneBadge, zoneLabel } from "./shared";

/**
 * Every material the latest run planned, paged in the database — the place to
 * look up one that has nothing to order, or to sort the whole plan by cover,
 * spend or lead time. Clicking a row explains it.
 */
export function AllMaterialsTab({ onExplain }: { onExplain: (partId: number | null, warehouseId?: number | null) => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);

  const table = useServerTable<ReplenishmentLineDTO>({
    queryKey: [...replenishmentKeys.lines, "all"],
    fetchPage: (request) => client.find(request),
    fetchTotal: () => client.count(),
  });

  const cols = useMemo(() => {
    const c = defineColumns<ReplenishmentLineDTO>();
    // Numbers stay numbers for the range filter and the sort; they are shown grouped and trimmed.
    const quantity = (id: string, accessor: (row: ReplenishmentLineDTO) => number | null, options: Parameters<typeof c.custom>[3]) =>
      c.custom(id, accessor, (row) => <span className="tabular-nums">{formatQuantity(accessor(row))}</span>, { align: "right", ...options });
    // The server filters on the enum; people read the words the badges use.
    const options = (values: readonly string[], label: (value: string) => string) => values.map((value) => ({ value, label: label(value) }));
    return [
      c.custom("zone", (row) => row.zone, (row) => <ZoneBadge zone={row.zone} />, {
        headerKey: "replenishment.column.zone",
        filter: "multiselect",
        filterOptions: options(REPLENISHMENT_ZONES, (value) => zoneLabel(value as (typeof REPLENISHMENT_ZONES)[number], t)),
        priority: 1,
      }),
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name, { headerKey: "column.part.name", priority: 2 }),
      c.text("part_group_code", (row) => row.part_group_code ?? "", { headerKey: "column.part.group", priority: 3 }),
      c.custom(
        "supplier_name",
        (row) => row.supplier_name ?? "",
        (row) => <SupplierLink id={row.supplier_id}>{row.supplier_name ?? ""}</SupplierLink>,
        { headerKey: "column.po.supplier", priority: 2 },
      ),
      c.custom("replenishment_policy", (row) => row.replenishment_policy, (row) => policyLabel(row.replenishment_policy, t), {
        headerKey: "replenishment.column.policy",
        filter: "multiselect",
        filterOptions: options(REPLENISHMENT_POLICIES, (value) => policyLabel(value, t)),
      }),
      quantity("average_daily_usage", (row) => row.average_daily_usage, { headerKey: "replenishment.column.adu", filter: "range" }),
      quantity("on_hand_quantity", (row) => row.on_hand_quantity, { headerKey: "replenishment.column.on-hand", filter: "range", priority: 3 }),
      quantity("position_quantity", (row) => row.position_quantity, { headerKey: "replenishment.column.position", filter: "range" }),
      c.custom(
        "days_of_cover",
        (row) => row.days_of_cover ?? "",
        (row) => <span className="tabular-nums">{formatCover(row.days_of_cover === null ? null : Number(row.days_of_cover), t)}</span>,
        { headerKey: "replenishment.column.cover", filter: "range" },
      ),
      c.number("lead_time_days", (row) => row.lead_time_days, { headerKey: "replenishment.column.lead", filter: "range", defaultVisible: false }),
      quantity("order_quantity", (row) => row.order_quantity, { headerKey: "replenishment.column.order", filter: "range", priority: 2 }),
      c.custom("order_amount_cad", (row) => row.order_amount_cad, (row) => <span className="tabular-nums">{formatCad(row.order_amount_cad)}</span>, {
        headerKey: "replenishment.column.amount",
        filter: "range",
        align: "right",
      }),
      c.text("warehouse_code", (row) => row.warehouse_code, { headerKey: "column.stock.warehouse", defaultVisible: false }),
    ];
  }, [t]);

  return (
    <DataTable<ReplenishmentLineDTO>
      data={table.rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      stateKey="/purchasing/replenishment/all"
      loading={table.loading}
      server={table.mode}
      onRowClick={(row) => onExplain(row.part_id, row.warehouse_id)}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}
